/**
 * FormEffectCurveEditor — the coach-authored Form-effect curve control
 * (Goal Score PR-2/H2, wired into the Studio by PR-7b).
 *
 * Replaces, in its entirety, the earlier "Boost/Penalty/Neutral" +
 * "Low/Medium/High" chip-row design that was superseded by the owner's
 * 2026-10-18 ruling (`docs/goal_score/H2_form_effect_curve.md` §5,
 * `REPORT.md` §2.4/§6.1). One card, "How does Form affect your Goal
 * points?":
 *
 *   1. Anchor table (Form -> Multiplier), 2-10 points.
 *   2. Maximum multiplier field.
 *   3. Live curve preview (backend-computed sample points ONLY — this
 *      component never runs PCHIP client-side, H2 §1.5's determinism
 *      argument).
 *   4. "Use the standard setting" (Neutral) preset button.
 *   5. One-sentence worked preview, generated by the SAME preview call the
 *      chart uses.
 *   6. Plain-language validation (client-side mirror of
 *      `FormEffectCurve`'s own Pydantic validator — the backend's 422 is
 *      still the final word; this is an inline UX improvement, not a
 *      replacement for server-side validation).
 */

import { useEffect, useRef, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { previewFormEffectCurve } from '@/services/goalAuthoringApi';
import {
  DEFAULT_FORM_EFFECT_CURVE,
  NEUTRAL_FORM_EFFECT_CURVE,
  FORM_EFFECT_MIN_ANCHORS,
  FORM_EFFECT_MAX_ANCHORS,
  FORM_EFFECT_MAX_MULTIPLIER_CEILING,
  type FormEffectCurve,
  type FormEffectAnchor,
} from '@/types/goalAuthoring';
import {
  CURVE_CARD_TITLE,
  CURVE_ADD_POINT_LABEL,
  CURVE_ADD_POINT_LIMIT_NOTE,
  CURVE_REMOVE_POINT_FLOOR_NOTE,
  CURVE_MAX_MULTIPLIER_LABEL,
  CURVE_MAX_MULTIPLIER_NOTE,
  curveMaxMultiplierNoNewPointNote,
  CURVE_NEUTRAL_PRESET_LABEL,
  curveWorkedPreviewSentence,
  CURVE_NEUTRAL_WORKED_PREVIEW_SUFFIX,
  curveTopQuartileLimitationNote,
  CURVE_ERROR_FORM_NOT_INCREASING,
  CURVE_ERROR_MULTIPLIER_DECREASING,
  CURVE_ERROR_MULTIPLIER_BELOW_ZERO,
  curveErrorAboveMaxMultiplier,
  CURVE_ERROR_POINT_COUNT,
  CURVE_EMPTY_ROW_NOTE,
} from '@/utils/goalAuthoringCopy';

const PREVIEW_DEBOUNCE_MS = 250;
const SAMPLE_FORMS = Array.from({ length: 101 }, (_, i) => i); // 0..100 integers

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; samples: Array<[number, number]> }
  | { status: 'error' };

export interface FormEffectCurveEditorProps {
  value: FormEffectCurve | undefined;
  onChange: (value: FormEffectCurve) => void;
  templateId?: string;
  /** The template's demo Form score (if one exists) — the worked-preview
   * sentence uses this when available, else the median anchor (H2 §5
   * item 5). */
  demoFormScore?: number | null;
  className?: string;
}

function isNeutral(curve: FormEffectCurve | undefined): boolean {
  if (!curve) return false;
  if (curve.anchors.length !== 2) return false;
  const [a, b] = curve.anchors;
  return a.form === 0 && a.multiplier === 1.0 && b.form === 100 && b.multiplier === 1.0;
}

/** Client-side mirror of `FormEffectCurve`'s Pydantic validator (H2 §3.1) —
 * same rules, same plain-language messages. The backend 422 is still the
 * final word (H2 §5's own framing); this only gives inline feedback. */
function validateCurve(curve: FormEffectCurve): string | null {
  const forms = curve.anchors.map((a) => a.form);
  const mults = curve.anchors.map((a) => a.multiplier);
  if (curve.anchors.length < FORM_EFFECT_MIN_ANCHORS || curve.anchors.length > FORM_EFFECT_MAX_ANCHORS) {
    return CURVE_ERROR_POINT_COUNT;
  }
  for (let i = 1; i < forms.length; i++) {
    if (forms[i] <= forms[i - 1]) return CURVE_ERROR_FORM_NOT_INCREASING;
  }
  for (let i = 1; i < mults.length; i++) {
    if (mults[i] < mults[i - 1]) return CURVE_ERROR_MULTIPLIER_DECREASING;
  }
  if (mults.some((m) => m < 0)) return CURVE_ERROR_MULTIPLIER_BELOW_ZERO;
  if (mults.some((m) => m > curve.max_multiplier)) return curveErrorAboveMaxMultiplier(curve.max_multiplier);
  return null;
}

export default function FormEffectCurveEditor({
  value,
  onChange,
  templateId,
  demoFormScore = null,
  className,
}: FormEffectCurveEditorProps) {
  const curve = value ?? DEFAULT_FORM_EFFECT_CURVE;
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const requestIdRef = useRef(0);

  const validationError = validateCurve(curve);

  // Live preview + worked-preview sentence — debounced, backend-computed
  // only (H2 §1.5). Fires whenever the curve shape changes, never on every
  // keystroke in isolation (the debounce coalesces a burst of edits).
  useEffect(() => {
    if (!templateId || validationError) {
      setPreview({ status: 'idle' });
      return;
    }
    const myRequestId = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      setPreview({ status: 'loading' });
      previewFormEffectCurve(templateId, { curve, forms: SAMPLE_FORMS })
        .then((data) => {
          if (requestIdRef.current !== myRequestId) return;
          setPreview({ status: 'ok', samples: data.samples });
        })
        .catch(() => {
          if (requestIdRef.current !== myRequestId) return;
          setPreview({ status: 'error' });
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, JSON.stringify(curve), validationError]);

  const sampleAt = (form: number): number | null => {
    if (preview.status !== 'ok') return null;
    const match = preview.samples.find(([f]) => Math.round(f) === Math.round(form));
    return match ? match[1] : null;
  };

  const handleAnchorChange = (index: number, field: 'form' | 'multiplier', raw: string) => {
    const parsed = raw.trim() === '' ? null : Number(raw);
    const nextAnchors = curve.anchors.map((a, i) =>
      i === index ? { ...a, [field]: parsed ?? a[field] } : a,
    );
    onChange({ ...curve, anchors: nextAnchors });
  };

  const handleAddPoint = () => {
    if (curve.anchors.length >= FORM_EFFECT_MAX_ANCHORS) return;
    const last = curve.anchors[curve.anchors.length - 1];
    const nextForm = Math.min(100, (last?.form ?? 0) + 1);
    const nextAnchor: FormEffectAnchor = { form: nextForm, multiplier: last?.multiplier ?? 1.0 };
    onChange({ ...curve, anchors: [...curve.anchors, nextAnchor] });
  };

  const handleRemovePoint = (index: number) => {
    if (curve.anchors.length <= FORM_EFFECT_MIN_ANCHORS) return;
    onChange({ ...curve, anchors: curve.anchors.filter((_, i) => i !== index) });
  };

  const handleMaxMultiplierChange = (raw: string) => {
    const parsed = raw.trim() === '' ? null : Number(raw);
    if (parsed === null || !Number.isFinite(parsed)) return;
    onChange({ ...curve, max_multiplier: Math.min(FORM_EFFECT_MAX_MULTIPLIER_CEILING, Math.max(0.01, parsed)) });
  };

  const handleNeutralPreset = () => {
    onChange(NEUTRAL_FORM_EFFECT_CURVE);
  };

  const topAnchor = curve.anchors[curve.anchors.length - 1];
  const maxMultiplierHasNoNewPoint =
    !!topAnchor && curve.max_multiplier > topAnchor.multiplier;

  const neutral = isNeutral(curve);

  const previewForm = demoFormScore ?? (curve.anchors.length > 0
    ? curve.anchors[Math.floor(curve.anchors.length / 2)].form
    : null);
  const previewMultiplier = previewForm !== null ? sampleAt(previewForm) : null;

  return (
    <div
      className={cn('space-y-3 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3', className)}
      data-testid="form-effect-curve-editor"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-text-primary">{CURVE_CARD_TITLE}</p>
        <button
          type="button"
          onClick={handleNeutralPreset}
          className="text-xs font-medium px-2.5 py-1 rounded-md border border-border-subtle text-text-secondary hover:bg-dark-elevated-2 transition-colors"
          data-testid="curve-neutral-preset-button"
        >
          {CURVE_NEUTRAL_PRESET_LABEL}
        </button>
      </div>

      {/* Anchor table */}
      <div className="space-y-1.5" data-testid="curve-anchor-table">
        {curve.anchors.map((anchor, index) => {
          const rowIncomplete = !Number.isFinite(anchor.form) || !Number.isFinite(anchor.multiplier);
          return (
            <div key={index} className="flex items-center gap-2" data-testid={`curve-anchor-row-${index}`}>
              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-1.5 py-1',
                  rowIncomplete ? 'border-dashed border-border-subtle' : 'border-border-subtle',
                )}
              >
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={anchor.form}
                  onChange={(e) => handleAnchorChange(index, 'form', e.target.value)}
                  className="w-16 px-1.5 py-1 bg-dark-card text-text-primary text-sm text-center rounded"
                  aria-label={`Point ${index + 1} Form value`}
                  data-testid={`curve-anchor-form-input-${index}`}
                />
                <span className="text-xs text-text-muted">Form →</span>
                <input
                  type="number"
                  min={0}
                  max={FORM_EFFECT_MAX_MULTIPLIER_CEILING}
                  step={0.01}
                  value={anchor.multiplier}
                  onChange={(e) => handleAnchorChange(index, 'multiplier', e.target.value)}
                  className="w-16 px-1.5 py-1 bg-dark-card text-text-primary text-sm text-center rounded"
                  aria-label={`Point ${index + 1} multiplier`}
                  data-testid={`curve-anchor-multiplier-input-${index}`}
                />
                <span className="text-xs text-text-muted">×</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemovePoint(index)}
                disabled={curve.anchors.length <= FORM_EFFECT_MIN_ANCHORS}
                className="text-xs text-text-muted hover:text-danger disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`curve-remove-point-${index}`}
              >
                Remove
              </button>
              {rowIncomplete && <span className="text-[11px] text-text-muted">{CURVE_EMPTY_ROW_NOTE}</span>}
            </div>
          );
        })}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleAddPoint}
            disabled={curve.anchors.length >= FORM_EFFECT_MAX_ANCHORS}
            className="text-xs font-medium text-accent-indigo hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
            data-testid="curve-add-point-button"
          >
            + {CURVE_ADD_POINT_LABEL}
          </button>
          <span className="text-[11px] text-text-muted">
            {curve.anchors.length >= FORM_EFFECT_MAX_ANCHORS
              ? CURVE_ADD_POINT_LIMIT_NOTE
              : curve.anchors.length <= FORM_EFFECT_MIN_ANCHORS
              ? CURVE_REMOVE_POINT_FLOOR_NOTE
              : ''}
          </span>
        </div>
      </div>

      {validationError && (
        <p className="text-xs text-danger" data-testid="curve-validation-error">
          {validationError}
        </p>
      )}

      {/* Maximum multiplier */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-text-secondary" htmlFor="curve-max-multiplier">
          {CURVE_MAX_MULTIPLIER_LABEL}
        </label>
        <input
          id="curve-max-multiplier"
          type="number"
          min={0.01}
          max={FORM_EFFECT_MAX_MULTIPLIER_CEILING}
          step={0.1}
          value={curve.max_multiplier}
          onChange={(e) => handleMaxMultiplierChange(e.target.value)}
          className="w-24 px-2 py-1.5 bg-dark-card text-text-primary text-sm text-center rounded border border-border-subtle"
          data-testid="curve-max-multiplier-input"
        />
        <p className="text-[11px] text-text-muted">{CURVE_MAX_MULTIPLIER_NOTE}</p>
        {maxMultiplierHasNoNewPoint && topAnchor && (
          <p className="text-[11px] text-info" data-testid="curve-max-multiplier-no-new-point-note">
            {curveMaxMultiplierNoNewPointNote(topAnchor.multiplier)}
          </p>
        )}
      </div>

      {/* Live curve preview */}
      <div className="rounded-md bg-dark-card p-2" data-testid="curve-preview-chart">
        {preview.status === 'loading' && (
          <p className="text-xs text-text-muted">Measuring…</p>
        )}
        {preview.status === 'error' && (
          <p className="text-xs text-danger">
            We couldn&apos;t draw this curve just now. Try again in a moment.
          </p>
        )}
        {preview.status === 'ok' && (
          <CurveSvg samples={preview.samples} maxMultiplier={curve.max_multiplier} />
        )}
      </div>

      {/* Worked-preview sentence */}
      <p className="text-xs text-text-secondary" data-testid="curve-worked-preview-sentence">
        {curveWorkedPreviewSentence(previewForm, previewMultiplier)}
        {neutral && <span className="text-text-muted"> {CURVE_NEUTRAL_WORKED_PREVIEW_SUFFIX}</span>}
      </p>

      {/* Top-quartile clamp limitation note (H2 §4.4) — only meaningful for
          the exactly-3-point default curve. */}
      {curve.anchors.length === 3 && topAnchor && (
        <div className="flex items-start gap-2 p-2 bg-dark-card rounded text-[11px] text-text-secondary">
          <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted" />
          <span>{curveTopQuartileLimitationNote(topAnchor.form, topAnchor.multiplier)}</span>
        </div>
      )}
    </div>
  );
}

function CurveSvg({
  samples,
  maxMultiplier,
}: {
  samples: Array<[number, number]>;
  maxMultiplier: number;
}) {
  const width = 280;
  const height = 100;
  const padding = 8;
  const points = samples
    .map(([form, multiplier]) => {
      const x = padding + (form / 100) * (width - 2 * padding);
      const y = height - padding - (multiplier / Math.max(maxMultiplier, 0.01)) * (height - 2 * padding);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Form-effect curve preview">
      <polyline points={points} fill="none" stroke="var(--color-accent-indigo, #6366F1)" strokeWidth={2} />
    </svg>
  );
}
